<template>
  <div class="doc-page">
    <el-container class="doc-layout">
      <!-- 左侧目录树 -->
      <el-aside width="280px" class="doc-sidebar">
        <div class="sidebar-header">
          <div class="sidebar-title">
            <el-icon :size="18"><Collection /></el-icon>
            <span>文档管理</span>
          </div>
          <el-button type="primary" size="small" @click="openAddCategoryDialog">
            <el-icon><Plus /></el-icon>
            <span>新建目录</span>
          </el-button>
        </div>

        <div class="sidebar-search">
          <el-input v-model="filterText" placeholder="搜索目录或文档" clearable>
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>

        <div class="sidebar-actions">
          <el-button text size="small" @click="clearSelection">
            <el-icon><RefreshLeft /></el-icon>
            <span>清除选中</span>
          </el-button>
        </div>

        <div class="tree-wrap">
          <el-empty v-if="treeData.length === 0" description="暂无目录，点击上方按钮创建" :image-size="90">
            <el-button type="primary" size="small" @click="openAddCategoryDialog">
              <el-icon><Plus /></el-icon>
              <span>新建目录</span>
            </el-button>
          </el-empty>

          <el-tree
            v-else
            ref="treeRef"
            :data="treeData"
            node-key="uniqueId"
            :props="{ children: 'children', label: 'name' }"
            :filter-node-method="filterNode"
            :default-expanded-keys="defaultExpandedKeys"
            :draggable="true"
            highlight-current
            @node-click="handleNodeClick"
            @node-drag-start="handleDragStart"
            @node-drop="handleDrop"
          >
            <template #default="{ data }">
              <span
                class="tree-node"
                @mouseenter="handleNodeHover(data)"
                @mouseleave="handleNodeLeave(data)"
              >
                <el-icon class="tree-node-icon">
                  <Folder v-if="data.type === 'folder'" />
                  <Grid v-else-if="data.documentType === 'excel'" />
                  <Document v-else />
                </el-icon>
                <span class="tree-node-label" :title="data.name">{{ data.name }}</span>
                <el-tag
                  v-if="data.type === 'document' && data.documentType === 'excel'"
                  size="small"
                  type="warning"
                  effect="plain"
                  class="tree-type-tag"
                >表</el-tag>
                <span v-if="hoveredNodeId === data.id" class="tree-node-actions" @click.stop>
                  <el-icon class="node-action" title="编辑" @click.stop="openEditDialog(data)"><Edit /></el-icon>
                  <el-icon class="node-action danger" title="删除" @click.stop="confirmDelete(data)"><Delete /></el-icon>
                </span>
              </span>
            </template>
          </el-tree>
        </div>
      </el-aside>

      <!-- 右侧内容区 -->
      <el-container class="doc-main-wrap">
        <el-header class="doc-header" height="56px">
          <div class="header-left">
            <el-icon :size="18">
              <Grid v-if="type === 'document' && currentDocumentType === 'excel'" />
              <Document v-else />
            </el-icon>
            <span class="header-title">{{ currentCategoryName || '文档管理' }}</span>
            <el-tag v-if="type === 'document'" size="small" :type="currentDocumentType === 'excel' ? 'warning' : 'primary'" effect="light">
              {{ currentDocumentType === 'excel' ? 'Excel 表格' : '文档' }}
            </el-tag>
            <el-tag v-else-if="type === 'folder'" size="small" type="primary" effect="light">目录</el-tag>
          </div>
          <div class="header-right">
            <el-button text @click="$router.push('/')">
              <el-icon><HomeFilled /></el-icon>
              <span>返回首页</span>
            </el-button>
          </div>
        </el-header>

        <el-main class="doc-main">
          <!-- 选中目录 -->
          <div v-if="type === 'folder'" class="folder-empty">
            <el-empty description="已进入该目录，请从左侧选择文档查看内容" :image-size="100">
              <el-button type="primary" @click="openAddDocumentDialog">
                <el-icon><Plus /></el-icon>
                <span>添加文档</span>
              </el-button>
            </el-empty>
          </div>

          <!-- 选中文档 -->
          <div v-else-if="type === 'document'" class="doc-content-card">
            <div v-if="!isEditing" class="doc-toolbar">
              <div class="toolbar-left">
                <h3 class="doc-title">{{ currentCategoryName }}</h3>
                <span v-if="currentDocumentUpdatedTime" class="doc-meta">更新于 {{ currentDocumentUpdatedTime }}</span>
              </div>
              <div class="toolbar-actions">
                <template v-if="currentDocumentType !== 'excel'">
                  <el-dropdown v-if="currentDocumentContent" @command="exportRichText">
                    <el-button size="small">
                      <el-icon><Download /></el-icon>
                      <span>导出</span>
                      <el-icon><ArrowDown /></el-icon>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item command="word">导出 Word（.doc）</el-dropdown-item>
                        <el-dropdown-item command="html">导出 HTML</el-dropdown-item>
                        <el-dropdown-item command="txt">导出 TXT</el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <el-button v-if="currentDocumentContent" size="small" @click="copyDocumentContent">
                    <el-icon><CopyDocument /></el-icon>
                    <span>复制</span>
                  </el-button>
                </template>
                <el-button size="small" @click="reloadDocument">
                  <el-icon><Refresh /></el-icon>
                  <span>刷新</span>
                </el-button>
                <el-button type="primary" @click="startEditing">
                  <el-icon><EditPen /></el-icon>
                  <span>编辑文档</span>
                </el-button>
              </div>
            </div>

            <div v-if="!isEditing && currentDocumentType !== 'excel' && currentDocumentContent" class="doc-stats">
              <span>{{ docStats.chars }} 字</span>
              <span>约 {{ docStats.words }} 词</span>
            </div>

            <div v-if="!isEditing && currentDocumentType === 'excel'" class="doc-excel-view">
              <ExcelEditor :model-value="currentDocumentContent" :file-name="currentCategoryName" readonly />
            </div>
            <div v-else-if="!isEditing && currentDocumentType !== 'excel' && currentDocumentContent" class="document-content" v-html="currentDocumentContent"></div>
            <div v-else-if="!isEditing && !currentDocumentContent" class="doc-empty-content">
              <el-empty description="文档暂无内容，点击右上角「编辑文档」开始写作" :image-size="90" />
            </div>

            <!-- 编辑器区域 -->
            <div v-if="isEditing" class="doc-editor">
              <template v-if="currentDocumentType === 'excel'">
                <ExcelEditor
                  ref="excelEditor"
                  :model-value="currentDocumentContent"
                  :file-name="currentCategoryName"
                />
              </template>

              <template v-else>
                <div class="ai-toolbar">
                  <el-button type="primary" size="small" :loading="aiCompleting" @click="aiComplete">
                    <el-icon><MagicStick /></el-icon>
                    <span>AI 续写</span>
                  </el-button>
                  <el-button type="success" size="small" @click="openAiGenerateDialog">
                    <el-icon><DocumentAdd /></el-icon>
                    <span>AI 生成</span>
                  </el-button>
                  <el-button type="warning" size="small" @click="openAiAskDialog">
                    <el-icon><ChatDotRound /></el-icon>
                    <span>AI 提问</span>
                  </el-button>
                  <el-button v-if="aiCompleting || aiGenerating" type="danger" size="small" @click="stopAi">
                    <el-icon><VideoPause /></el-icon>
                    <span>停止</span>
                  </el-button>
                  <span class="ai-auto-complete">
                    <el-switch v-model="autoCompleteEnabled" size="small" />
                    自动联想
                  </span>
                  <span class="ai-shortcut-tip">快捷键：Ctrl+J 续写</span>
                </div>

                <div class="editor-import-bar">
                  <el-button size="small" @click="triggerRichTextImport">
                    <el-icon><Upload /></el-icon>
                    <span>导入文件</span>
                  </el-button>
                  <span class="import-tip">支持 .html / .txt / .md 文件</span>
                  <input
                    ref="richtextImportInput"
                    type="file"
                    accept=".html,.htm,.txt,.md"
                    class="hidden-input"
                    @change="onRichTextImport"
                  />
                </div>

                <quill-editor
                  ref="quillEditor"
                  class="editor"
                  theme="snow"
                  :options="editorOptions"
                  v-model:content="editDocumentContent"
                  contentType="html"
                ></quill-editor>
                <input
                  ref="quillImageInput"
                  type="file"
                  accept="image/*"
                  class="hidden-input"
                  @change="onQuillImageChange"
                />
              </template>

              <div class="editor-actions">
                <el-button type="primary" @click="updateDocument">
                  <el-icon><Check /></el-icon>
                  <span>保存</span>
                </el-button>
                <el-button @click="cancelEdit">
                  <el-icon><Close /></el-icon>
                  <span>取消</span>
                </el-button>
              </div>
            </div>
          </div>

          <!-- 未选择 -->
          <div v-else class="doc-empty">
            <el-empty description="请选择左侧的目录或文档开始浏览" :image-size="120" />
          </div>
        </el-main>
      </el-container>
    </el-container>

    <!-- 编辑目录 / 文档弹窗 -->
    <el-dialog v-model="editDocumentOrCategoryDialogVisible" title="编辑" class="doc-dialog" width="440px">
      <el-form v-if="editTargetType === 'folder'" :model="newCategory" label-position="top">
        <el-form-item label="名称" :required="true">
          <el-input v-model="newCategory.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="排序号" :required="true">
          <el-input v-model="newCategory.sortNo" type="number" placeholder="请输入排序号" />
        </el-form-item>
      </el-form>
      <el-form v-if="editTargetType === 'document'" :model="newDocument" label-position="top">
        <el-form-item label="文档名称" :required="true">
          <el-input v-model="newDocument.name" placeholder="请输入文档名称" />
        </el-form-item>
        <el-form-item label="排序号" :required="true">
          <el-input v-model="newDocument.sortNo" type="number" placeholder="请输入排序号" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDocumentOrCategoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateDocumentOrCategory">确定</el-button>
      </template>
    </el-dialog>

    <!-- 添加目录弹窗 -->
    <el-dialog v-model="addCategoryDialogVisible" title="添加目录" class="doc-dialog" width="440px">
      <el-form :model="newCategory" label-position="top">
        <el-form-item label="目录名称" :required="true">
          <el-input v-model="newCategory.name" placeholder="请输入目录名称" />
        </el-form-item>
        <el-form-item label="目录等级">
          <el-input v-model="newCategory.level" type="number" placeholder="选填" />
        </el-form-item>
        <el-form-item label="排序号" :required="true">
          <el-input v-model="newCategory.sortNo" type="number" placeholder="请输入排序号" />
        </el-form-item>
        <el-form-item label="扩展信息">
          <el-input v-model="newCategory.extraInfo" placeholder="选填" />
        </el-form-item>
        <el-form-item v-show="false" label="父目录">
          <el-input v-model="newCategory.parentId" />
        </el-form-item>
        <el-form-item v-show="false" label="用户ID">
          <el-input v-model="newCategory.uid" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addCategoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="addCategory">确定</el-button>
      </template>
    </el-dialog>

    <!-- 添加文档弹窗 -->
    <el-dialog v-model="addDocumentDialogVisible" title="添加文档" class="doc-dialog" width="440px">
      <el-form :model="newDocument" label-position="top">
        <el-form-item label="文档名称" :required="true">
          <el-input v-model="newDocument.name" placeholder="请输入文档名称" />
        </el-form-item>
        <el-form-item label="文档类型">
          <el-radio-group v-model="newDocument.documentType">
            <el-radio-button value="richtext">富文本</el-radio-button>
            <el-radio-button value="excel">Excel 表格</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序号" :required="true">
          <el-input v-model="newDocument.sortNo" type="number" placeholder="请输入排序号" />
        </el-form-item>
        <el-form-item v-show="false" label="所属目录">
          <el-input v-model="newDocument.categoryId" />
        </el-form-item>
        <el-form-item v-show="false" label="用户ID">
          <el-input v-model="newDocument.uid" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDocumentDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="addDocument">确定</el-button>
      </template>
    </el-dialog>

    <!-- AI 生成弹窗 -->
    <el-dialog v-model="aiGenerateDialogVisible" title="AI 生成内容" class="doc-dialog" width="480px">
      <el-form label-position="top">
        <el-form-item label="写作主题" required>
          <el-input v-model="aiGeneratePrompt" type="textarea" :rows="4"
                    placeholder="例如：介绍西红柿的营养价值和挑选方法" />
        </el-form-item>
        <el-form-item label="内容长度">
          <el-select v-model="aiGenerateLength" style="width: 100%;">
            <el-option label="约 200 字" :value="200" />
            <el-option label="约 500 字" :value="500" />
            <el-option label="约 1000 字" :value="1000" />
          </el-select>
        </el-form-item>
        <el-form-item label="插入位置">
          <el-radio-group v-model="aiGenerateInsertPos">
            <el-radio value="cursor">光标处</el-radio>
            <el-radio value="end">文末</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="aiGenerateDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="aiGenerating" @click="aiGenerate">开始生成</el-button>
      </template>
    </el-dialog>

    <!-- AI 提问弹窗 -->
    <el-dialog v-model="aiAskDialogVisible" title="AI 提问" class="doc-dialog" width="640px" @closed="resetAiAsk">
      <el-input v-model="aiAskQuestion" type="textarea" :rows="3"
                placeholder="例如：我有一个 api.jar，如何用 nohup 命令部署？" />
      <div class="ai-ask-toolbar">
        <el-button type="primary" size="small" :loading="aiAsking" @click="aiAsk">
          <el-icon><Promotion /></el-icon>
          <span>提问</span>
        </el-button>
        <el-button v-if="aiAsking" type="danger" size="small" @click="stopAi">
          <el-icon><VideoPause /></el-icon>
          <span>停止</span>
        </el-button>
        <el-button v-if="aiAskAnswer && !aiAsking" type="success" size="small" @click="insertAiAnswer">
          <el-icon><Check /></el-icon>
          <span>插入到文档</span>
        </el-button>
        <el-button v-if="aiAskAnswer && !aiAsking" size="small" @click="aiAskAnswer = ''">
          <el-icon><Delete /></el-icon>
          <span>清空回答</span>
        </el-button>
      </div>
      <div v-if="aiAskAnswer" class="ai-ask-answer">{{ aiAskAnswer }}</div>
      <div v-else-if="aiAsking" class="ai-ask-answer ai-ask-pending">正在思考…</div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { QuillEditor } from '@vueup/vue-quill'
import { streamAi } from '@/api/ai';
import ExcelEditor from '@/components/document/ExcelEditor.vue';

export default {
  name: 'DocumentIndex',
  components: {
    QuillEditor,
    ExcelEditor
  },
  data() {
    return {
      treeData: [],
      defaultExpandedKeys: [],
      filterText: '',
      editorOptions: {
        placeholder: '在这里输入文档内容…',
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, 4, 5, false] }],
              [{ font: [] }],
              [{ size: ['small', false, 'large', 'huge'] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ align: [] }],
              ['blockquote', 'code-block'],
              ['link', 'image', 'video'],
              ['table'],
              ['undo', 'redo'],
              ['clean']
            ],
            handlers: {
              image: () => this.handleQuillImage(),
              table: () => this.handleQuillTable(),
              undo: function () {
                this.quill.history.undo();
              },
              redo: function () {
                this.quill.history.redo();
              },
            },
          },
          history: {
            delay: 500,
            maxStack: 100,
          },
        },
      },
      newCategory: {
        name: '',
        level: '',
        sortNo: '',
        extraInfo: '',
        parentId: 0,
        uid: 0
      },
      newDocument: {
        name: '',
        sortNo: '',
        categoryId: '',
        uid: 0,
        documentType: 'richtext'
      },
      editDocumentContentObject: {
        content: '',
        uid: 0
      },
      addCategoryDialogVisible: false,
      addDocumentDialogVisible: false,
      currentCategoryId: null,
      currentDocumentId: null,
      currentCategoryName: '',
      currentDocumentContent: '', // 当前选中文档的内容
      currentDocumentType: 'richtext',
      currentDocumentUpdatedTime: '',
      type: '',
      editTargetType: '',
      editDocumentContent: '',
      hoveredNodeId: null,
      editDocumentOrCategoryDialogVisible: false,
      isEditing: false,
      aiCompleting: false,
      aiGenerating: false,
      autoCompleteEnabled: false,
      autoCompleteTimer: null,
      aiAbortController: null,
      lastAiInsertTime: 0,
      quillKeyHandler: null,
      aiGenerateDialogVisible: false,
      aiGeneratePrompt: '',
      aiGenerateLength: 500,
      aiGenerateInsertPos: 'cursor',
      aiAskDialogVisible: false,
      aiAskQuestion: '',
      aiAskAnswer: '',
      aiAsking: false,
    };
  },
  computed: {
    docStats() {
      const text = this.getPlainText(this.currentDocumentContent);
      const chars = text.replace(/\s/g, '').length;
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      return { chars, words };
    },
  },
  watch: {
    editDocumentContent() {
      if (this.autoCompleteEnabled && this.isEditing) {
        this.scheduleAutoComplete();
      }
    },
    isEditing(val) {
      if (val) {
        this.$nextTick(() => this.attachQuillShortcut());
      } else {
        this.detachQuillShortcut();
        if (this.autoCompleteTimer) {
          clearTimeout(this.autoCompleteTimer);
          this.autoCompleteTimer = null;
        }
        this.stopAi();
      }
    },
    filterText(val) {
      if (this.$refs.treeRef) {
        this.$refs.treeRef.filter(val);
      }
    },
  },
  mounted() {
    this.loadCategories();
  },
  beforeUnmount() {
    this.detachQuillShortcut();
    if (this.autoCompleteTimer) {
      clearTimeout(this.autoCompleteTimer);
      this.autoCompleteTimer = null;
    }
    if (this.aiAbortController) {
      this.aiAbortController.abort();
    }
  },
  methods: {
    getQuill() {
      return this.$refs.quillEditor ? this.$refs.quillEditor.getQuill() : null;
    },
    attachQuillShortcut() {
      const quill = this.getQuill();
      if (quill && !this.quillKeyHandler) {
        this.quillKeyHandler = (e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'j') {
            e.preventDefault();
            this.aiComplete();
          }
        };
        quill.root.addEventListener('keydown', this.quillKeyHandler);
      }
    },
    detachQuillShortcut() {
      const quill = this.getQuill();
      if (quill && this.quillKeyHandler) {
        quill.root.removeEventListener('keydown', this.quillKeyHandler);
        this.quillKeyHandler = null;
      }
    },
    handleQuillImage() {
      this.$refs.quillImageInput.click();
    },
    onQuillImageChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = this.getQuill();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', reader.result, 'user');
        quill.setSelection(range.index + 1, 0);
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    handleQuillTable() {
      const quill = this.getQuill();
      if (!quill) return;
      const table = quill.getModule('table');
      if (table) {
        table.insertTable(2, 2);
      } else {
        this.$message.warning('当前编辑器不支持表格');
      }
    },
    scheduleAutoComplete() {
      if (this.autoCompleteTimer) {
        clearTimeout(this.autoCompleteTimer);
      }
      this.autoCompleteTimer = setTimeout(() => this.tryAutoComplete(), 3000);
    },
    tryAutoComplete() {
      this.autoCompleteTimer = null;
      if (!this.autoCompleteEnabled || !this.isEditing || this.aiCompleting || this.aiGenerating || this.aiAsking) {
        return;
      }
      const quill = this.getQuill();
      if (!quill) {
        return;
      }
      const text = quill.getText();
      if (text.trim().length < 20) {
        return;
      }
      const sel = quill.getSelection();
      if (!sel || sel.index < text.length - 3) {
        return;
      }
      if (Date.now() - this.lastAiInsertTime < 8000) {
        return;
      }
      this.doAiComplete(200);
    },
    aiComplete() {
      if (this.aiCompleting) {
        return;
      }
      const quill = this.getQuill();
      if (!quill) {
        this.$message.warning('请先进入编辑状态');
        return;
      }
      const sel = quill.getSelection();
      const textBefore = quill.getText(0, sel ? sel.index : quill.getLength() - 1);
      if (!textBefore.trim()) {
        this.$message.warning('请先输入一些内容再续写');
        return;
      }
      this.doAiComplete(300);
    },
    doAiComplete(maxTokens) {
      const quill = this.getQuill();
      const sel = quill.getSelection();
      const insertAt = sel ? sel.index : quill.getLength() - 1;
      const textBefore = quill.getText(0, insertAt);
      this.startAiStream('/api/ai/complete', { content: textBefore, maxTokens }, insertAt, 'complete');
    },
    openAiGenerateDialog() {
      if (!this.isEditing) {
        this.$message.warning('请先进入编辑状态');
        return;
      }
      this.aiGenerateDialogVisible = true;
    },
    aiGenerate() {
      const prompt = this.aiGeneratePrompt.trim();
      if (!prompt) {
        this.$message.warning('请输入写作主题');
        return;
      }
      this.aiGenerateDialogVisible = false;
      const quill = this.getQuill();
      const sel = quill.getSelection();
      const len = quill.getLength();
      const insertAt = this.aiGenerateInsertPos === 'cursor' && sel ? sel.index : len - 1;
      const maxTokens = Math.min(4096, Math.round(this.aiGenerateLength * 2));
      this.startAiStream('/api/ai/generate', { content: prompt, maxTokens }, insertAt, 'generate');
    },
    openAiAskDialog() {
      if (!this.isEditing) {
        this.$message.warning('请先进入编辑状态');
        return;
      }
      this.aiAskDialogVisible = true;
    },
    async aiAsk() {
      const question = this.aiAskQuestion.trim();
      if (!question) {
        this.$message.warning('请输入问题');
        return;
      }
      this.aiAskAnswer = '';
      this.aiAsking = true;
      const controller = new AbortController();
      this.aiAbortController = controller;
      try {
        await streamAi('/api/ai/ask', { content: question, maxTokens: 2048 }, {
          signal: controller.signal,
          onDelta: (chunk) => {
            if (chunk) {
              this.aiAskAnswer += chunk;
            }
          },
        });
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          this.$message.error(err.message || 'AI 服务异常');
        }
      } finally {
        this.aiAsking = false;
        this.aiAbortController = null;
      }
    },
    insertAiAnswer() {
      const answer = this.aiAskAnswer.trim();
      if (!answer) {
        return;
      }
      const quill = this.getQuill();
      const sel = quill.getSelection();
      const insertAt = sel ? sel.index : quill.getLength() - 1;
      quill.insertText(insertAt, '\n' + answer + '\n');
      quill.setSelection(insertAt + answer.length + 2, 0);
      this.aiAskDialogVisible = false;
      this.$message.success('已插入到文档');
    },
    resetAiAsk() {
      this.aiAskQuestion = '';
      this.aiAskAnswer = '';
      this.aiAsking = false;
    },
    async startAiStream(endpoint, params, insertAt, mode) {
      const quill = this.getQuill();
      const flag = mode === 'generate' ? 'aiGenerating' : 'aiCompleting';
      this[flag] = true;
      const controller = new AbortController();
      this.aiAbortController = controller;
      let inserted = '';
      try {
        await streamAi(endpoint, params, {
          signal: controller.signal,
          onDelta: (chunk) => {
            if (!chunk) {
              return;
            }
            quill.insertText(insertAt + inserted.length, chunk);
            quill.setSelection(insertAt + inserted.length + chunk.length, 0);
            inserted += chunk;
          },
        });
        this.lastAiInsertTime = Date.now();
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          this.$message.error(err.message || 'AI 服务异常');
        }
      } finally {
        this[flag] = false;
        this.aiAbortController = null;
      }
    },
    stopAi() {
      if (this.aiAbortController) {
        this.aiAbortController.abort();
        this.aiAbortController = null;
      }
      this.aiCompleting = false;
      this.aiGenerating = false;
      this.aiAsking = false;
    },
    handleDragStart(draggingNode) {
      this.draggingNode = draggingNode;
    },
    async handleDrop(draggingNode, dropNode) {
      try {
        const response = await axios.put(`/api/documentCategory/${draggingNode.data.id}/move`, { parentId: dropNode.data.id, type: draggingNode.data.type});

        if (response.data.code === 0) {
          this.$message.success('移动成功');
          this.loadCategories();
        } else {
          this.$message.error(response.data.message);
        }
      } catch (error) {
        console.error('移动失败:', error);
      }
    },
    clearSelection() {
      this.currentCategoryId = null;
      this.currentDocumentId = null;
      this.currentDocumentContent = '';
      this.currentDocumentType = 'richtext';
      this.currentDocumentUpdatedTime = '';
      this.currentCategoryName = '';
      this.type = '';
      this.hoveredNodeId = null;
      this.$nextTick(() => {
        if (this.$refs.treeRef) {
          this.$refs.treeRef.setCurrentKey(null);
        }
      });
    },
    filterNode(value, data) {
      if (!value) return true;
      return data.name && data.name.toLowerCase().includes(value.toLowerCase());
    },
    handleNodeHover(data) {
      this.hoveredNodeId = data.id;
    },
    handleNodeLeave(data) {
      if (this.hoveredNodeId === data.id) {
        this.hoveredNodeId = null;
      }
    },
    startEditing() {
      this.editDocumentContent = this.currentDocumentContent;
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editDocumentContent = '';
    },
    async updateDocument() {
      try {
        if (this.currentDocumentType === 'excel' && this.$refs.excelEditor) {
          this.editDocumentContent = this.$refs.excelEditor.getContent();
        }
        this.editDocumentContentObject = {
          content: this.editDocumentContent,
          documentType: this.currentDocumentType
        };
        const response = await axios.put(`/api/document/${this.currentDocumentId}`, this.editDocumentContentObject);
        if (response.data.code !== 0) {
          this.$message.error(response.data.message);
          return;
        }
        this.isEditing = false;
        this.currentDocumentContent = this.editDocumentContent;
        this.$message.success('保存成功');
        this.loadDocumentContent(this.currentDocumentId);
      } catch (error) {
        console.error('更新文档失败:', error);
      }
    },
    async loadCategories() {
      const that = this
      that.defaultExpandedKeys = [];
      // 记录展开的节点
      if (that.$refs.treeRef) {
        const nodeMap = that.$refs.treeRef.store.nodesMap;
        Object.keys(nodeMap).forEach(key => {
          const node = nodeMap[key];
          if (node.expanded) {
            that.defaultExpandedKeys.push(key);
          }
        });
      }
      try {
        const response = await axios.get('/api/documentCategory/categories');
        const categories = response.data.data.sort((a, b) => a.sortNo - b.sortNo);

        const allDocuments = {};
        for (let category of categories) {
          const documents = await that.loadDocuments(category.id);
          allDocuments[category.id] = documents.map(doc => ({ ...doc, type: 'document', uniqueId: 'document_' + doc.id }));
        }

        that.treeData = that.buildCategoryTree(categories, allDocuments);

        // 重新设置展开的节点
        that.$nextTick(() => {
          if (that.currentCategoryId !== null && that.currentCategoryId !== undefined && that.currentCategoryId !== "" &&
              that.$refs.treeRef !== null && that.$refs.treeRef !== undefined && that.$refs.treeRef !== "") {
            that.$refs.treeRef.setCurrentKey(that.type + '_' + that.currentCategoryId)
          }
        });
      } catch (error) {
        console.error('加载目录失败:', error);
      }
    },
    buildCategoryTree(categories, allDocuments) {
      const that = this
      const map = {};
      const tree = [];
      if (categories.length === 0) {
        this.currentCategoryName = null;
        this.currentCategoryId = null;
        this.type = '';
        return tree;
      }
      categories.forEach(category => {
        map[category.id] = { ...category, type: 'folder', uniqueId: 'folder_' + category.id, children: allDocuments[category.id] || [] };
      });
      categories.forEach(category => {
        if (category.parentId === 0) {
          tree.push(map[category.id]);
          that.currentCategoryName = category.name;
        } else {
          map[category.parentId].children.push(map[category.id]);
        }
      });
      return tree;
    },
    async loadDocuments(categoryId) {
      try {
        const response = await axios.get(`/api/document/documents?categoryId=${categoryId}`);
        return response.data.data.sort((a, b) => a.sortNo - b.sortNo);
      } catch (error) {
        console.error('加载文档失败:', error);
        return [];
      }
    },
    handleNodeClick(nodeData) {
      this.hoveredNodeId = nodeData.id
      if (nodeData.type === 'folder') {
        this.currentCategoryId = nodeData.id;
        this.currentCategoryName = nodeData.name;
        this.currentDocumentContent = '';
        this.type = 'folder';
      } else if (nodeData.type === 'document') {
        this.currentDocumentId = nodeData.id;
        this.currentCategoryId = nodeData.id;
        this.type = 'document';
        this.currentCategoryName = nodeData.name;
        this.currentDocumentType = nodeData.documentType || 'richtext';
        this.loadDocumentContent(nodeData.id);
      }
    },
    async loadDocumentContent(documentId) {
      try {
        const response = await axios.get(`/api/document/${documentId}`);
        this.isEditing = false;
        this.currentDocumentContent = response.data.data.content;
        this.currentDocumentType = response.data.data.documentType || 'richtext';
        this.currentDocumentUpdatedTime = response.data.data.updatedTime
          ? dayjs(response.data.data.updatedTime).format('YYYY-MM-DD HH:mm')
          : '';
      } catch (error) {
        console.error('加载文档内容失败:', error);
      }
    },
    openAddCategoryDialog() {
      const parentId = this.type === 'folder' ? this.currentCategoryId : 0;
      const parentNode = parentId ? this.findTreeNode(this.treeData, parentId, 'folder') : null;
      const siblings = parentNode
        ? (parentNode.children || []).filter(n => n.type === 'folder')
        : this.treeData;
      this.newCategory = {
        name: '',
        level: '',
        sortNo: this.getNextSortNo(siblings),
        extraInfo: '',
        parentId,
      };
      this.addCategoryDialogVisible = true;
    },
    async addCategory() {
      try {
        const response = await axios.post('/api/documentCategory', this.newCategory);
        if (response.data.code !== 0) {
          this.$message.error(response.data.message);
          return;
        }
        this.addCategoryDialogVisible = false;
        this.$message.success('目录创建成功');
        this.loadCategories();
      } catch (error) {
        console.error('添加目录失败:', error);
      }
    },
    openAddDocumentDialog() {
      const folderNode = this.findTreeNode(this.treeData, this.currentCategoryId, 'folder');
      const documents = folderNode
        ? (folderNode.children || []).filter(n => n.type === 'document')
        : [];
      this.newDocument = {
        name: '',
        sortNo: this.getNextSortNo(documents),
        categoryId: this.currentCategoryId,
        documentType: 'richtext',
      };
      this.addDocumentDialogVisible = true;
    },
    findTreeNode(nodes, id, type) {
      for (const node of nodes || []) {
        if (node.id === id && node.type === type) {
          return node;
        }
        const found = this.findTreeNode(node.children, id, type);
        if (found) {
          return found;
        }
      }
      return null;
    },
    getNextSortNo(nodes) {
      const max = (nodes || []).reduce((m, n) => Math.max(m, Number(n.sortNo) || 0), 0);
      return max + 1;
    },
    async addDocument() {
      try {
        const response = await axios.post('/api/document', this.newDocument);
        if (response.data.code !== 0) {
          this.$message.error(response.data.message);
          return;
        }
        this.$message.success('文档创建成功');
        this.loadCategories();
        this.addDocumentDialogVisible = false;
      } catch (error) {
        console.error('添加文档失败:', error);
      }
    },
    confirmDelete(nodeData) {
      this.$confirm(`确定要删除「${nodeData.name}」吗？删除后不可恢复。`, '删除确认', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          let response = null;
          if (nodeData.type === 'folder') {
            response = await axios.delete(`/api/documentCategory/${nodeData.id}`);
          } else if (nodeData.type === 'document') {
            response = await axios.delete(`/api/document/${nodeData.id}`);
          }
          if (response.data.code !== 0) {
            this.$message.error(response.data.message);
            return;
          }
          this.$message.success('删除成功');
          this.currentCategoryId = null;
          this.currentCategoryName = null;
          this.type = null;
          this.hoveredNodeId = null;
          this.loadCategories();
        } catch (error) {
          console.error('删除失败:', error);
        }
      }).catch(() => {});
    },
    openEditDialog(nodeData) {
      this.editTargetType = nodeData.type;
      if (nodeData.type === 'folder') {
        this.newCategory = {
          id: nodeData.id,
          name: nodeData.name,
          sortNo: nodeData.sortNo,
        };
        this.editDocumentOrCategoryDialogVisible = true;
      } else if (nodeData.type === 'document') {
        this.newDocument = {
          id: nodeData.id,
          name: nodeData.name,
          sortNo: nodeData.sortNo,
        };
        this.editDocumentOrCategoryDialogVisible = true;
      }
    },
    async updateDocumentOrCategory() {
      try {
        if (this.editTargetType === 'folder') {
          await axios.put(`/api/documentCategory/${this.newCategory.id}`, this.newCategory);
        } else if (this.editTargetType === 'document' && this.newDocument.id) {
          await axios.put(`/api/document/${this.newDocument.id}`, this.newDocument);
        }
        this.$message.success('更新成功');
        this.loadCategories();
        this.editDocumentOrCategoryDialogVisible = false;
      } catch (error) {
        console.error('更新失败:', error);
      }
    },
    reloadDocument() {
      if (this.currentDocumentId) {
        this.loadDocumentContent(this.currentDocumentId);
        this.$message.success('已刷新');
      }
    },
    getPlainText(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.innerText || '';
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    markdownToHtml(md) {
      const inline = (s) => s
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank">$1</a>');

      const lines = md
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .split('\n');
      const blocks = [];
      let i = 0;
      while (i < lines.length) {
        const line = lines[i];
        if (/^```/.test(line)) {
          const codeLines = [];
          i++;
          while (i < lines.length && !/^```/.test(lines[i])) {
            codeLines.push(lines[i]);
            i++;
          }
          i++;
          blocks.push(`<pre><code>${codeLines.join('\n')}</code></pre>`);
          continue;
        }
        const listLines = [];
        while (i < lines.length && /^[-*] /.test(lines[i])) {
          listLines.push(lines[i].replace(/^[-*] /, ''));
          i++;
        }
        if (listLines.length) {
          blocks.push(`<ul>${listLines.map(li => `<li>${inline(li)}</li>`).join('')}</ul>`);
          continue;
        }
        const heading = line.match(/^(#{1,4})\s+(.*)$/);
        if (heading) {
          const level = heading[1].length;
          blocks.push(`<h${level}>${inline(heading[2])}</h${level}>`);
          i++;
          continue;
        }
        if (line.trim() === '') {
          i++;
          continue;
        }
        const paraLines = [];
        while (
          i < lines.length &&
          lines[i].trim() !== '' &&
          !/^[-*] /.test(lines[i]) &&
          !/^#{1,4}\s/.test(lines[i]) &&
          !/^```/.test(lines[i])
        ) {
          paraLines.push(lines[i]);
          i++;
        }
        blocks.push(`<p>${paraLines.map(inline).join('<br>')}</p>`);
      }
      return blocks.join('\n');
    },
    triggerRichTextImport() {
      this.$refs.richtextImportInput.click();
    },
    async onRichTextImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const text = await file.text();
      if (ext === 'md') {
        this.editDocumentContent = this.markdownToHtml(text);
      } else if (ext === 'txt') {
        this.editDocumentContent = text
          .split(/\r?\n/)
          .map(line => line.trim() ? `<p>${this.escapeHtml(line)}</p>` : '')
          .join('');
      } else {
        this.editDocumentContent = text;
      }
      this.$message.success('文件已导入，编辑完成后记得保存');
      event.target.value = '';
    },
    downloadBlob(content, filename, mime) {
      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    exportRichText(command) {
      const name = this.currentCategoryName || '文档';
      if (!this.currentDocumentContent) {
        this.$message.warning('文档暂无内容');
        return;
      }
      if (command === 'txt') {
        this.downloadBlob(this.getPlainText(this.currentDocumentContent), `${name}.txt`, 'text/plain;charset=utf-8');
      } else if (command === 'html') {
        const html = `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="utf-8"><title>${name}</title><style>body{max-width:820px;margin:40px auto;padding:0 20px;font-family:-apple-system,'PingFang SC','Microsoft YaHei',sans-serif;line-height:1.8;color:#333}img{max-width:100%}</style></head><body>${this.currentDocumentContent}</body></html>`;
        this.downloadBlob(html, `${name}.html`, 'text/html;charset=utf-8');
      } else if (command === 'word') {
        const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${name}</title></head><body>${this.currentDocumentContent}</body></html>`;
        this.downloadBlob(html, `${name}.doc`, 'application/msword');
      }
    },
    copyDocumentContent() {
      const text = this.getPlainText(this.currentDocumentContent);
      if (!text) {
        this.$message.warning('文档暂无内容');
        return;
      }
      navigator.clipboard.writeText(text)
        .then(() => this.$message.success('已复制到剪贴板'))
        .catch(() => this.$message.error('复制失败，请手动选择复制'));
    },
    getLogin() {
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.doc-page {
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.doc-layout {
  height: 100%;
}

/* ===== 侧边栏 ===== */
.doc-sidebar {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e8ecf3;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 12px;
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.sidebar-title .el-icon {
  color: var(--el-color-primary);
}

.sidebar-header .el-button {
  margin: 0;
}

.sidebar-search {
  padding: 0 16px 10px;
}

.sidebar-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 8px;
}

.sidebar-actions .el-button {
  margin: 0;
}

.tree-wrap {
  flex: 1;
  overflow: auto;
  padding: 0 8px 12px;
}

.tree-wrap .el-empty {
  padding: 40px 12px;
}

:deep(.el-tree) {
  background: transparent;
  --el-tree-node-hover-bg-color: #f2f4fa;
}

:deep(.el-tree-node__content) {
  height: 36px;
  border-radius: 8px;
  margin: 2px 0;
  padding-right: 6px;
}

:deep(.el-tree-node.is-current > .el-tree-node__content) {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  font-weight: 500;
}

.tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.tree-node-icon {
  flex-shrink: 0;
  color: #8a919f;
}

.tree-node.is-current .tree-node-icon,
:deep(.el-tree-node.is-current) .tree-node-icon {
  color: var(--el-color-primary);
}

.tree-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.tree-type-tag {
  flex-shrink: 0;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  font-size: 11px;
  margin-right: 2px;
}

.tree-node-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.node-action {
  font-size: 14px;
  color: #8a919f;
  padding: 3px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
}

.node-action:hover {
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.node-action.danger:hover {
  color: #f56c6c;
  background: #fef0f0;
}

/* ===== 顶部栏 ===== */
.doc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e8ecf3;
  padding: 0 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.header-left .el-icon {
  color: var(--el-color-primary);
}

.header-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-right .el-button {
  margin: 0;
}

/* ===== 主内容区 ===== */
.doc-main {
  background: #f0f2f5;
  padding: 20px;
  overflow: auto;
}

.doc-empty,
.folder-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 96px);
  background: #ffffff;
  border-radius: 12px;
}

.doc-content-card {
  max-width: 980px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04);
  padding: 28px 32px;
  min-height: calc(100vh - 96px);
  box-sizing: border-box;
}

.doc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid #f0f2f5;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.toolbar-actions .el-button {
  margin: 0;
}

.doc-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2329;
}

.doc-meta {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.doc-stats {
  display: flex;
  gap: 16px;
  margin: -8px 0 14px;
  font-size: 12px;
  color: #909399;
}

.doc-empty-content {
  padding: 40px 0;
}

.doc-excel-view {
  width: 100%;
}

.document-content {
  text-align: left;
  line-height: 1.8;
  font-size: 15px;
  color: #333333;
  word-break: break-word;
}

.document-content :deep(h1),
.document-content :deep(h2),
.document-content :deep(h3),
.document-content :deep(h4) {
  color: #1f2329;
  margin: 1.2em 0 0.6em;
  line-height: 1.4;
}

.document-content :deep(h1) { font-size: 26px; }
.document-content :deep(h2) { font-size: 22px; }
.document-content :deep(h3) { font-size: 18px; }
.document-content :deep(h4) { font-size: 16px; }

.document-content :deep(p) {
  margin: 0.8em 0;
}

.document-content :deep(img) {
  max-width: 100%;
  border-radius: 8px;
}

.document-content :deep(a) {
  color: var(--el-color-primary);
  text-decoration: none;
}

.document-content :deep(a:hover) {
  text-decoration: underline;
}

.document-content :deep(ul),
.document-content :deep(ol) {
  padding-left: 1.6em;
}

.document-content :deep(blockquote) {
  margin: 1em 0;
  padding: 8px 16px;
  border-left: 3px solid var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  border-radius: 0 8px 8px 0;
  color: #555f6d;
}

.document-content :deep(pre) {
  background: #f6f8fa;
  padding: 14px 16px;
  border-radius: 8px;
  overflow: auto;
  font-size: 13px;
}

.document-content :deep(code) {
  background: #f0f2f5;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 0.92em;
}

.document-content :deep(pre code) {
  background: transparent;
  padding: 0;
}

.document-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

.document-content :deep(th),
.document-content :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
}

.document-content :deep(th) {
  background: #f8fafc;
  font-weight: 600;
}

/* ===== 编辑器 ===== */
.doc-editor {
  width: 100%;
}

.ai-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #f8faff;
  border: 1px solid #e7ecf7;
  border-radius: 10px;
}

.ai-toolbar .el-button {
  margin: 0;
}

.ai-auto-complete {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
}

.ai-shortcut-tip {
  font-size: 12px;
  color: #909399;
}

.editor-import-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.editor-import-bar .el-button {
  margin: 0;
}

.import-tip {
  font-size: 12px;
  color: #909399;
}

.hidden-input {
  display: none;
}

.editor :deep(.ql-toolbar.ql-snow) {
  border-radius: 10px 10px 0 0;
  border-color: #dcdfe6;
  background: #fafbfc;
}

.editor :deep(.ql-container.ql-snow) {
  border-color: #dcdfe6;
  border-radius: 0 0 10px 10px;
  font-size: 15px;
}

.editor :deep(.ql-toolbar button.ql-undo::after),
.editor :deep(.ql-toolbar button.ql-redo::after) {
  font-size: 17px;
  line-height: 18px;
  color: #444;
}

.editor :deep(.ql-toolbar button.ql-undo::after) {
  content: '↺';
}

.editor :deep(.ql-toolbar button.ql-redo::after) {
  content: '↻';
}

.editor :deep(.ql-editor) {
  min-height: 340px;
  line-height: 1.8;
  text-align: left;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.editor-actions .el-button {
  margin: 0;
}

/* ===== AI 提问 ===== */
.ai-ask-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
}

.ai-ask-toolbar .el-button {
  margin: 0;
}

.ai-ask-answer {
  max-height: 320px;
  overflow: auto;
  padding: 14px 16px;
  background: #f8faff;
  border: 1px solid #e7ecf7;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-ask-pending {
  color: #909399;
}
</style>

<style>
.doc-dialog {
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.doc-dialog .el-dialog__header {
  padding: 20px 24px 0;
}

.doc-dialog .el-dialog__title {
  font-size: 17px;
  font-weight: 600;
  color: #1f2329;
}

.doc-dialog .el-dialog__body {
  padding: 20px 24px;
}

.doc-dialog .el-dialog__footer {
  padding: 0 24px 20px;
}

.doc-dialog .el-form-item__label {
  font-weight: 500;
  color: #1f2329;
}
</style>
